import { ThemeEvents } from '@theme/events';

const GROUP_SELECTOR = '[data-free-shipping-visibility-rule]';

/**
 * @param {ParentNode} scope
 * @returns {HTMLElement[]}
 */
function getManagedGroups(scope = document) {
  return Array.from(scope.querySelectorAll(GROUP_SELECTOR)).filter((element) => element instanceof HTMLElement);
}

/**
 * @param {HTMLElement} group
 * @returns {number | null}
 */
function getThresholdCents(group) {
  const raw = group.dataset.freeShippingThresholdCents || '';
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * @param {HTMLElement} group
 * @param {number} cartTotalCents
 */
function applyVisibilityRule(group, cartTotalCents) {
  const rule = group.dataset.freeShippingVisibilityRule;
  if (!rule || !Number.isFinite(cartTotalCents)) return;

  const thresholdCents = getThresholdCents(group);
  if (thresholdCents == null || thresholdCents <= 0) return;

  const shouldHide =
    rule === 'show'
      ? cartTotalCents < thresholdCents
      : rule === 'hide'
      ? cartTotalCents >= thresholdCents
      : false;

  group.classList.toggle('hidden', shouldHide);
  group.toggleAttribute('hidden', shouldHide);
}

/**
 * @param {number} cartTotalCents
 * @param {ParentNode} [scope]
 */
function updateGroupsForCartTotal(cartTotalCents, scope = document) {
  if (!Number.isFinite(cartTotalCents)) return;
  getManagedGroups(scope).forEach((group) => applyVisibilityRule(group, cartTotalCents));
}

/**
 * @returns {number | null}
 */
function getInitialCartTotalCents() {
  const raw = window.Theme?.cart?.totalPriceCents;
  const parsed = Number.parseInt(`${raw ?? ''}`, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * @returns {Promise<number | null>}
 */
async function fetchCartTotalCents() {
  const cartRoot = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${cartRoot}cart.js`);
  if (!response.ok) return null;

  const cart = await response.json();
  const totalPrice = Number.parseInt(`${cart?.total_price ?? ''}`, 10);
  return Number.isFinite(totalPrice) ? totalPrice : null;
}

/**
 * @param {Event} event
 * @returns {number | null}
 */
function getCartTotalCentsFromEvent(event) {
  const cartEvent = /** @type {any} */ (event);
  const totalPrice = Number.parseInt(`${cartEvent?.detail?.resource?.total_price ?? ''}`, 10);
  return Number.isFinite(totalPrice) ? totalPrice : null;
}

/**
 * @param {ParentNode} [scope]
 */
async function refreshFreeShippingVisibility(scope = document) {
  let cartTotalCents = getInitialCartTotalCents();

  if (cartTotalCents == null) {
    try {
      cartTotalCents = await fetchCartTotalCents();
    } catch (error) {
      console.warn('Failed to fetch cart total for free shipping visibility', error);
      return;
    }
  }

  if (cartTotalCents == null) return;

  if (window.Theme?.cart) {
    window.Theme.cart.totalPriceCents = cartTotalCents;
  }

  updateGroupsForCartTotal(cartTotalCents, scope);
}

function initializeFreeShippingVisibility() {
  refreshFreeShippingVisibility();
}

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  let cartTotalCents = getCartTotalCentsFromEvent(event);

  if (cartTotalCents == null) {
    try {
      cartTotalCents = await fetchCartTotalCents();
    } catch (error) {
      console.warn('Failed to fetch cart total for free shipping visibility', error);
      return;
    }
  }

  if (cartTotalCents == null) return;

  if (window.Theme?.cart) {
    window.Theme.cart.totalPriceCents = cartTotalCents;
  }

  updateGroupsForCartTotal(cartTotalCents);
  // Re-apply after cart section morphs replace group blocks in the DOM.
  requestAnimationFrame(() => updateGroupsForCartTotal(cartTotalCents));
});

// Optional cross-context hook (e.g., cart integrations) for reuse.
document.addEventListener('free-shipping:context-update', (event) => {
  const customEvent = /** @type {any} */ (event);
  const cartTotalCents = Number.parseInt(
    `${customEvent?.detail?.cartTotalCents ?? customEvent?.detail?.priceCents ?? ''}`,
    10
  );
  const scope =
    customEvent?.detail?.scope instanceof Element || customEvent?.detail?.scope instanceof Document
      ? customEvent.detail.scope
      : document;
  if (!Number.isFinite(cartTotalCents)) {
    refreshFreeShippingVisibility(scope);
    return;
  }
  if (window.Theme?.cart) {
    window.Theme.cart.totalPriceCents = cartTotalCents;
  }
  updateGroupsForCartTotal(cartTotalCents, scope);
});

document.addEventListener('theme:dynamic-content', (event) => {
  const customEvent = /** @type {any} */ (event);
  const scope =
    customEvent?.detail?.scope instanceof Element || customEvent?.detail?.scope instanceof Document
      ? customEvent.detail.scope
      : document;
  refreshFreeShippingVisibility(scope);
});

initializeFreeShippingVisibility();
